import { useCallback, useEffect, useState } from 'react';
import { CustomTab, CustomTabScope, CustomTabType } from './types';
import { DEFAULT_ICON_KEY } from './customTabIcons';

const STORAGE_KEY = 'pos_custom_tabs';

function applyDefaults(tab: Partial<CustomTab>): CustomTab {
  return {
    id: tab.id ?? crypto.randomUUID(),
    label: tab.label ?? '',
    description: tab.description ?? '',
    icon: tab.icon ?? DEFAULT_ICON_KEY,
    tabType: (tab.tabType as CustomTabType) ?? 'Link',
    url: tab.url,
    snippet: tab.snippet,
    openInNewTab: tab.openInNewTab !== false,
    scope: (tab.scope as CustomTabScope) ?? 'TopLevel',
  };
}

function loadTabs(): CustomTab[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(applyDefaults);
  } catch {
    return [];
  }
}

function saveTabs(tabs: CustomTab[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  } catch {
    // ignore storage errors
  }
}

export interface UseCustomTabsResult {
  tabs: CustomTab[];
  addTab: (tab: Omit<CustomTab, 'id'>) => CustomTab;
  updateTab: (id: string, updates: Partial<CustomTab>) => void;
  deleteTab: (id: string) => void;
  reorderTabs: (reordered: CustomTab[]) => void;
}

export function useCustomTabs(): UseCustomTabsResult {
  const [tabs, setTabs] = useState<CustomTab[]>(loadTabs);

  useEffect(() => {
    saveTabs(tabs);
  }, [tabs]);

  const addTab = useCallback((tab: Omit<CustomTab, 'id'>): CustomTab => {
    const newTab = applyDefaults({ ...tab, id: crypto.randomUUID() });
    setTabs((prev) => [...prev, newTab]);
    return newTab;
  }, []);

  const updateTab = useCallback(
    (id: string, updates: Partial<CustomTab>): void => {
      setTabs((prev) =>
        prev.map((t) => (t.id === id ? applyDefaults({ ...t, ...updates }) : t))
      );
    },
    []
  );

  const deleteTab = useCallback((id: string): void => {
    setTabs((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const reorderTabs = useCallback((reordered: CustomTab[]): void => {
    setTabs(reordered);
  }, []);

  return { tabs, addTab, updateTab, deleteTab, reorderTabs };
}
